import { useTranslation } from 'react-i18next'
import {
	isRouteErrorResponse,
	Link,
	useLocation,
	useRouteError,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { HeroBackdrop } from '#app/components/hero-backdrop.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { useLocalizedPath } from '#app/utils/i18n.ts'

/**
 * Error boundary that renders a 404 as a marketing hero and hands every
 * other error to `GeneralErrorBoundary`. The root loader has already
 * resolved the locale, so the root i18n instance renders it in the visitor's
 * language. The 404 bypasses `statusHandlers` because that wrapper's padded
 * container would box in the full-bleed hero.
 */
export function NotFoundErrorBoundary() {
	const error = useRouteError()
	if (isRouteErrorResponse(error) && error.status === 404) {
		return <NotFoundHero />
	}
	return <GeneralErrorBoundary />
}

function NotFoundHero() {
	const { t } = useTranslation()
	const location = useLocation()
	const localePath = useLocalizedPath()

	return (
		<section className="relative overflow-hidden pt-32 pb-24">
			<HeroBackdrop />

			<div className="relative z-10 container text-center">
				<h1 className="text-h2 md:text-h1 font-bold">
					<span className="text-gradient-brand">{t('notFound.heading')}</span>
				</h1>
				<p className="text-body-md text-muted-foreground md:text-body-lg mx-auto mt-6 max-w-2xl">
					{t('notFound.text')}
				</p>
				<p className="text-body-sm text-muted-foreground mt-2 break-all">
					{location.pathname}
				</p>
				<div className="mt-10 flex justify-center">
					<Button asChild size="lg">
						<Link to={localePath('/')}>
							<Icon name="arrow-left">{t('notFound.cta')}</Icon>
						</Link>
					</Button>
				</div>
			</div>
		</section>
	)
}
